import { endpoint } from "@uri/typed-api";
import { z } from "zod/v4";

const whatsappNumberPattern = /^\d{10,20}(-\d+)?(@(c\.us|g\.us|lid))?$/;

const whatsappNumber = z.string().refine(
  (val) => whatsappNumberPattern.test(val),
  "Must be a valid WhatsApp number or chat ID (10-20 digits, optionally ending with @c.us, @g.us, or @lid)",
);

export const apiDefinition = {
  clearCredentials: endpoint({
    authRequired: false,
    input: z.object({ phoneNumber: z.string(), token: z.string() }),
    output: z.object({
      success: z.boolean().optional(),
      error: z.string().optional(),
    }),
  }),
  deleteAccount: endpoint({
    authRequired: false,
    input: z.object({ phoneNumber: z.string(), token: z.string() }),
    output: z.object({ success: z.boolean() }),
  }),
  setWebhook: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      url: z.string().or(z.null()),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  sendMessage: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      toNumber: whatsappNumber,
      message: z.string(),
      linkPreview: z.boolean(),
      token: z.string(),
    }),
    output: z.object({ messageId: z.string() }),
  }),
  sendMedia: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      toNumber: whatsappNumber,
      base64: z.string(),
      type: z.enum(["image", "video", "document"]),
      mimeType: z.string().optional(),
      filename: z.string().optional(),
      caption: z.string().optional(),
      token: z.string(),
    }),
    output: z.object({ messageId: z.string() }),
  }),
  reconnect: endpoint({
    authRequired: false,
    input: z.object({ number: z.string(), token: z.string() }),
    output: z.union([z.object({}), z.object({ error: z.string() })]),
  }),
  triggerCodeLogin: endpoint({
    authRequired: false,
    input: z.object({ number: z.string(), token: z.string() }),
    output: z.union([z.object({}), z.object({ error: z.string() })]),
  }),
  addUserToWhatsAppGroup: endpoint({
    authRequired: false,
    input: z.object({
      groupId: z.string(),
      userId: z.string(),
      fromNumber: z.string(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  getGroups: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      adminOnly: z.boolean().optional(),
      token: z.string(),
    }),
    output: z.object({
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  }),
  getWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      token: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(false) }),
      z.object({
        success: z.literal(true),
        requests: z.array(z.object({
          requesterId: z.string(),
          addedById: z.string().optional(),
          parentGroupId: z.string().optional(),
          method: z.string(),
          timestamp: z.number(),
        })),
      }),
    ]),
  }),
  getCommonWhatsappGroups: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      userId: z.string(),
      token: z.string(),
    }),
    output: z.object({
      groups: z.array(z.object({ id: z.string(), name: z.string() })),
    }),
  }),
  approveWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      requesterIds: z.array(z.string()).nullable().optional(),
      token: z.string(),
    }),
    output: z.object({
      results: z.array(z.object({
        requesterId: z.string(),
        error: z.number().nullable(),
        message: z.string(),
      })),
    }),
  }),
  rejectWhatsappJoinRequests: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      requesterIds: z.array(z.string()),
      token: z.string(),
    }),
    output: z.object({
      results: z.array(z.object({
        requesterId: z.string(),
        error: z.number().nullable(),
        message: z.string(),
      })),
    }),
  }),
  sendTypingIndication: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      chatId: z.string(),
      durationMs: z.number().optional(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  stopTypingIndication: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      chatId: z.string(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  convertLidToNumber: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      lid: z.string(),
      token: z.string(),
    }),
    output: z.object({ number: z.string().nullable() }),
  }),
  changeWhatsappAccountName: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      name: z.string(),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  changeWhatsappAccountAvatar: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      imageBase64: z.string(),
      token: z.string(),
    }),
    output: z.object({}),
  }),
  editMessage: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      messageId: z.string(),
      newText: z.string(),
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  blockContact: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      contactId: whatsappNumber,
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  unblockContact: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: whatsappNumber,
      contactId: whatsappNumber,
      token: z.string(),
    }),
    output: z.object({ success: z.boolean() }),
  }),
  getGroupMembers: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupId: z.string(),
      token: z.string(),
    }),
    output: z.object({
      members: z.array(z.object({
        id: z.string(),
        isAdmin: z.boolean(),
        isSuperAdmin: z.boolean(),
      })),
    }),
  }),
  joinGroupByInviteLink: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupLink: z.string(),
      token: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        status: z.enum(["joined", "pending-approval"]),
        chatId: z.string(),
      }),
      z.object({
        success: z.literal(false),
        error: z.string(),
      }),
    ]),
  }),
  getWhatsappGroupDetails: endpoint({
    authRequired: false,
    input: z.object({
      fromNumber: z.string(),
      groupLink: z.string(),
      token: z.string(),
    }),
    output: z.union([
      z.object({
        success: z.literal(true),
        id: z.string(),
        subject: z.string(),
        desc: z.string(),
        size: z.number(),
        owner: z.string().optional(),
        isInGroup: z.boolean(),
        announce: z.boolean(),
        restrict: z.boolean(),
        creation: z.number(),
        suspended: z.boolean(),
        participants: z.array(z.object({
          id: z.string(),
          isAdmin: z.boolean(),
          isSuperAdmin: z.boolean(),
        })),
      }),
      z.object({
        success: z.literal(false),
        error: z.string(),
      }),
    ]),
  }),
} as const;

export const userApiDefinition = {
  purchaseNumber: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
    }),
    output: z.union([
      z.object({
        phoneNumber: z.string(),
        secretToken: z.string(),
        accountId: z.string(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
  createAccount: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
      phoneNumber: z.string(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  createCheckoutUrl: endpoint({
    authRequired: false,
    input: z.object({
      refreshToken: z.string(),
    }),
    output: z.union([
      z.object({ url: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
} as const;

export const adminApiDefinition = {
  getPaidCapacity: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ paidCapacity: z.number() }),
      z.object({ error: z.string() }),
    ]),
  }),
  setPaidCapacity: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      paidCapacity: z.number(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ success: z.literal(true) }),
      z.object({ error: z.string() }),
    ]),
  }),
  provisionAccount: endpoint({
    authRequired: false,
    input: z.object({
      email: z.string(),
      phoneNumber: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ secretToken: z.string(), accountId: z.string() }),
      z.object({ error: z.string() }),
    ]),
  }),
  triggerAdminCodeLogin: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({ error: z.string() }),
      z.object({ status: z.string() }),
      z.object({}),
    ]),
  }),
  getAccountStatus: endpoint({
    authRequired: false,
    input: z.object({
      phoneNumber: z.string(),
      email: z.string(),
      adminToken: z.string(),
    }),
    output: z.union([
      z.object({
        isConnected: z.boolean(),
        pairingCode: z.string().nullable(),
        verificationCode: z.string().nullable(),
        qrIssuedAt: z.number().nullable(),
      }),
      z.object({ error: z.string() }),
    ]),
  }),
} as const;

export const lineServerApiDefinition = apiDefinition;

export const fullApiDefinition = {
  ...apiDefinition,
  ...adminApiDefinition,
  ...userApiDefinition,
} as const;

export type LineServerApi = typeof lineServerApiDefinition;
export type AdminApi = typeof adminApiDefinition;
export type UserApi = typeof userApiDefinition;
export type Api = typeof apiDefinition;
export type FullApi = typeof fullApiDefinition;
